import * as React from "react"

import { cn } from "@/lib/utils"

/** For areas like instructions (multi-line, auto-expands with content) */
export const UnderlineTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function UnderlineTextarea(
  { className, onChange, onFocus, value, ...rest },
  ref,
) {
  const isReadOnlyLike = Boolean(rest.readOnly || rest.disabled)
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null)

  React.useImperativeHandle(
    ref,
    () => innerRef.current as HTMLTextAreaElement,
    [],
  )

  const resize = React.useCallback(() => {
    const el = innerRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }, [])

  React.useLayoutEffect(() => {
    resize()
  }, [resize, value])

  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (
    event,
  ) => {
    resize()
    if (onChange) onChange(event)
  }

  const handleFocus: React.FocusEventHandler<HTMLTextAreaElement> = (event) => {
    try {
      event.currentTarget.select()
    } catch {
      // textarea.select() should be supported, but guard just in case
    }
    if (onFocus) {
      onFocus(event)
    }
  }

  return (
    <textarea
      {...rest}
      ref={innerRef}
      value={value}
      className={cn(
        "jc-no-ios-zoom",
        "w-full min-h-8 text-sm text-slate-900 outline-none",
        isReadOnlyLike
          ? "rounded-md bg-slate-100 px-2 py-1 text-slate-600 shadow-[0_1px_0_0_rgba(203,213,225,1)]"
          : "bg-transparent shadow-[0_1px_0_0_rgba(148,163,184,1)] focus:shadow-[0_1px_0_0_rgba(15,23,42,1)]",
        "resize-none overflow-hidden",
        className,
      )}
      onChange={handleChange}
      onFocus={handleFocus}
    />
  )
})
